/**
 * Trip planner — multi-leg trip wizard.
 *
 * Steps:
 *   1. Define trip basics (purpose, cost centre)
 *   2. Compose legs (flight | hotel | train | bus) one at a time, each with
 *      auto-filtered results from /api/travel/search/* — only options within
 *      the employee's travel-category caps are shown.
 *   3. Review + submit → policy check runs → either goes to approval queue
 *      or is bounced back with hard-violation list.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell, { SectionCard } from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card } from "../components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { AirplaneTilt, Bed, Train, Bus, Trash, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { DateInput } from "../components/ui/date-input";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const TYPE_META = {
  flight: { label: "Flight", icon: AirplaneTilt, color: "text-sky-700" },
  hotel:  { label: "Hotel",  icon: Bed,          color: "text-pink-700" },
  train:  { label: "Train",  icon: Train,        color: "text-emerald-700" },
  bus:    { label: "Bus",    icon: Bus,          color: "text-amber-700" },
};

export default function TravelPlan() {
  const nav = useNavigate();
  const [cities, setCities] = useState([]);
  const [purpose, setPurpose] = useState("");
  const [costCentre, setCostCentre] = useState("");
  const [legs, setLegs] = useState([]);
  const [composer, setComposer] = useState(null);   // current leg being composed
  const [saving, setSaving] = useState(false);

  useEffect(() => { (async () => {
    try { setCities((await api.get("/travel/search/cities")).data); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  })(); }, []);

  const addLeg = (type) => setComposer({
    type, from_city: "", to_city: "", travel_date: "", return_date: "", pax: 1, rooms: 1, picked: null,
  });

  const removeLeg = (id) => setLegs(legs.filter(l => l.id !== id));

  const submit = async () => {
    if (!purpose) { toast.error("Purpose required"); return; }
    if (legs.length === 0) { toast.error("Add at least one leg"); return; }
    const start_date = legs.map(l => l.travel_date).filter(Boolean).sort()[0];
    const end_date = legs.map(l => l.return_date || l.travel_date).filter(Boolean).sort().slice(-1)[0];
    setSaving(true);
    try {
      const r = await api.post("/travel-requests", {
        purpose, cost_centre: costCentre, legs, start_date, end_date, payment_mode: "credit",
      });
      // Auto-submit so it hits policy check + approval flow
      try {
        await api.post(`/travel-requests/${r.data.id}/submit`, {});
        toast.success("Submitted for approval");
      } catch (e) {
        const d = e?.response?.data?.detail;
        if (d?.policy_check) {
          toast.error(`Hard policy block — ${d.policy_check.violations.length} violations. Saved as draft.`);
        } else { toast.error(formatApiError(d)); }
      }
      nav("/app/travel");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const total = legs.reduce((s, l) => s + Number(l.quoted_price || 0), 0);

  return (
    <AppShell title="Plan a trip">
      <div className="space-y-5">
        <SectionCard title="Trip details" data-testid="plan-step1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
            <div><Label>Purpose *</Label><Input value={purpose} onChange={e => setPurpose(e.target.value)} placeholder="Client visit, conference, etc." data-testid="plan-purpose"/></div>
            <div><Label>Cost centre</Label><Input value={costCentre} onChange={e => setCostCentre(e.target.value)} placeholder="optional"/></div>
          </div>
        </SectionCard>

        <SectionCard title={`Itinerary (${legs.length})`} subtitle="Add as many legs as you need — outbound flight + hotel + return flight + side trips. All within your eligibility limits.">
          <div className="flex gap-2 flex-wrap mb-4">
            {Object.entries(TYPE_META).map(([t, m]) => (
              <Button key={t} variant="outline" onClick={() => addLeg(t)} data-testid={`add-leg-${t}`}>
                <m.icon className="mr-1" size={16}/> Add {m.label}
              </Button>
            ))}
          </div>

          {legs.length === 0 && <div className="text-sm text-zinc-500 italic">No legs yet — start by adding a flight, hotel or train.</div>}

          <div className="space-y-2">
            {legs.map((l, i) => {
              const M = TYPE_META[l.type];
              return (
                <Card key={l.id} className="p-3 flex items-center gap-3" data-testid={`leg-${l.id}`}>
                  <div className={`${M.color} p-2 bg-zinc-50 rounded`}><M.icon size={20}/></div>
                  <div className="flex-1">
                    <div className="font-medium text-sm">{l.vendor_name}</div>
                    <div className="text-xs text-zinc-500">
                      {l.from_city || ""}{l.to_city ? ` → ${l.to_city}` : ""} · {l.travel_date}
                      {l.flight_class && ` · ${l.flight_class}`}
                      {l.train_class && ` · ${l.train_class}`}
                      {l.hotel_star && ` · ${l.hotel_star}★`}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold tabular-nums">{inr(l.quoted_price)}</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => removeLeg(l.id)}><Trash size={16}/></Button>
                </Card>
              );
            })}
          </div>

          {legs.length > 0 && (
            <div className="mt-4 p-3 bg-zinc-50 rounded flex justify-between items-center">
              <div className="text-sm text-zinc-600">Estimated total</div>
              <div className="text-xl font-bold tabular-nums">{inr(total)}</div>
            </div>
          )}
        </SectionCard>

        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={() => nav("/app/travel")}>Cancel</Button>
          <Button onClick={submit} disabled={saving || legs.length === 0} data-testid="plan-submit">
            {saving ? "Saving…" : "Submit for approval"}
          </Button>
        </div>
      </div>

      {composer && (
        <Composer composer={composer} setComposer={setComposer} cities={cities}
          onPick={(picked) => {
            setLegs([...legs, picked]);
            setComposer(null);
          }}/>
      )}
    </AppShell>
  );
}


// ──────────────────────── Per-leg search composer ───────────────────────
function Composer({ composer, setComposer, cities, onPick }) {
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const { type } = composer;
  const M = TYPE_META[type];

  const search = async () => {
    setSearching(true);
    setResults(null);
    try {
      let url, payload;
      if (type === "flight") {
        if (!composer.from_city || !composer.to_city || !composer.travel_date) { toast.error("From/To/Date required"); setSearching(false); return; }
        url = "/travel/search/flights";
        payload = { src: composer.from_city, dst: composer.to_city, date: composer.travel_date, pax: composer.pax };
      } else if (type === "hotel") {
        if (!composer.to_city || !composer.travel_date || !composer.return_date) { toast.error("City + check-in + check-out required"); setSearching(false); return; }
        url = "/travel/search/hotels";
        payload = { city: composer.to_city, checkin: composer.travel_date, checkout: composer.return_date, rooms: composer.rooms };
      } else if (type === "train") {
        url = "/travel/search/trains";
        payload = { src: composer.from_city, dst: composer.to_city, date: composer.travel_date, pax: composer.pax };
      } else {
        url = "/travel/search/buses";
        payload = { src: composer.from_city, dst: composer.to_city, date: composer.travel_date, pax: composer.pax };
      }
      const r = await api.post(url, payload);
      setResults(r.data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSearching(false); }
  };

  const pickResult = (res) => {
    let leg;
    if (type === "flight") {
      leg = { id: res.id, type, from_city: composer.from_city, to_city: composer.to_city,
              travel_date: composer.travel_date, vendor_name: `${res.airline} ${res.flight_no}`,
              flight_class: res.class, quoted_price: res.total_price, pax: composer.pax,
              depart: res.depart, arrive: res.arrive };
    } else if (type === "hotel") {
      leg = { id: res.id, type, from_city: composer.to_city, to_city: null,
              travel_date: composer.travel_date, return_date: composer.return_date,
              vendor_name: res.name, hotel_star: res.stars, rooms: composer.rooms,
              quoted_price: res.total_price };
    } else if (type === "train") {
      leg = { id: res.id, type, from_city: composer.from_city, to_city: composer.to_city,
              travel_date: composer.travel_date, vendor_name: `${res.operator} (${res.train_no})`,
              train_class: res.class, quoted_price: res.total_price, pax: composer.pax };
    } else {
      leg = { id: res.id, type, from_city: composer.from_city, to_city: composer.to_city,
              travel_date: composer.travel_date, vendor_name: `${res.operator} ${res.class}`,
              quoted_price: res.total_price, pax: composer.pax };
    }
    onPick(leg);
  };

  const needsTwoCities = type === "flight" || type === "train" || type === "bus";

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setComposer(null)}>
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="composer-modal">
        <div className="p-5 border-b flex items-center gap-3">
          <div className={`${M.color} p-2 bg-zinc-50 rounded`}><M.icon size={22}/></div>
          <div>
            <div className="font-semibold">Add {M.label}</div>
            <div className="text-xs text-zinc-500">Only options within your eligibility are shown.</div>
          </div>
        </div>
        <div className="p-5 space-y-3 border-b">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {needsTwoCities && (
              <>
                <CityPicker label="From" value={composer.from_city} cities={cities} onChange={v => setComposer({...composer, from_city: v})}/>
                <CityPicker label="To"   value={composer.to_city}   cities={cities} onChange={v => setComposer({...composer, to_city: v})}/>
              </>
            )}
            {type === "hotel" && <CityPicker label="City" value={composer.to_city} cities={cities} onChange={v => setComposer({...composer, to_city: v})}/>}
            <div>
              <Label>{type === "hotel" ? "Check-in" : "Travel date"}</Label>
              <DateInput value={composer.travel_date} onChange={e => setComposer({...composer, travel_date: e.target.value})} data-testid="composer-date"/>
            </div>
            {type === "hotel" && <div><Label>Check-out</Label><DateInput value={composer.return_date} onChange={e => setComposer({...composer, return_date: e.target.value})}/></div>}
          </div>
          <Button onClick={search} disabled={searching} data-testid="composer-search">
            {searching ? "Searching…" : "Search"}
          </Button>
          {results?.reason && <div className="text-sm text-red-600 mt-2">{results.reason}</div>}
          {results && !results.reason && (
            <div className="text-xs text-zinc-600">
              {results.total_before_filter} total · <span className="text-emerald-700 font-medium">{results.results.length} within your cap</span>
              {results.category_caps?.max_price ? ` · max ₹${results.category_caps.max_price}` : ""}
              {results.category_caps?.max_per_night ? ` · max ₹${results.category_caps.max_per_night}/night` : ""}
            </div>
          )}
        </div>
        <div className="p-5 space-y-2">
          {results?.results?.map(r => (
            <Card key={r.id} className="p-3 flex items-center gap-3 cursor-pointer hover:bg-zinc-50" onClick={() => pickResult(r)} data-testid={`result-${r.id}`}>
              <div className="flex-1">
                <div className="font-medium">{r.airline || r.operator || r.name}</div>
                <div className="text-xs text-zinc-500">
                  {r.depart && `${r.depart} → ${r.arrive || ""} · `}
                  {r.duration_min && `${Math.floor(r.duration_min/60)}h ${r.duration_min%60}m · `}
                  {r.duration_hours && `${r.duration_hours}h · `}
                  {r.class || ""}
                  {r.stars && `${r.stars}★ · rating ${r.rating}`}
                  {r.nights && ` · ${r.nights} nights`}
                </div>
              </div>
              <div className="text-right">
                <div className="font-semibold tabular-nums">{inr(r.total_price)}</div>
                {r.price_per_night && <div className="text-[10px] text-zinc-500">{inr(r.price_per_night)}/night</div>}
              </div>
              <Button size="sm">Pick</Button>
            </Card>
          ))}
        </div>
        <div className="p-4 border-t flex justify-end">
          <Button variant="outline" onClick={() => setComposer(null)}>Close</Button>
        </div>
      </div>
    </div>
  );
}

function CityPicker({ label, value, onChange, cities }) {
  return (
    <div>
      <Label>{label}</Label>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={`city-${label.toLowerCase()}`}><SelectValue placeholder="Choose…"/></SelectTrigger>
        <SelectContent>
          {cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
